import {ChangeDetectorRef, Component, computed, effect, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {ApiService} from "../../../services/api.service";
import {CustomFieldDTO} from "../../../DTO/CustomFieldDTO";
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {RecurrenceDTO} from "../../../DTO/RecurrenceDTO";
import {BonificationDTO} from "../../../DTO/BonificationDTO";
import {InscriptionSentDTO} from "../../../DTO/InscriptionSentDTO";
import Swal from "sweetalert2";
import {TranslateService} from "@ngx-translate/core";
import {InscriptionsDTO} from "../../../DTO/InscriptionsDTO";
import {FormBuilder, FormGroup} from "@angular/forms";
import { InscriptionsConstants } from '../constants';
import {CommunityService} from "../../../services/community/community.service";
import {ScreenSizeService} from "../../../services/screen-size.service";
import {debounceTime, firstValueFrom, Subject, Subscription} from 'rxjs';
import { PaymentResult, PaymentsService } from 'src/app/services/payments/payments.service';
import { PurchaseResultDTO } from 'src/app/DTO/tickets/PurchaseResultDTO';
import { InscriptionTotalPricesDTO } from 'src/app/DTO/prices/InscriptionTotalPricesDTO';
import { UploadedFileModel } from 'src/app/components/file-uploader/file-uploader.component';
import {SwiperContainer} from "swiper/element";
import {CardSelectOption} from "../../../components/cards-option-select/CardSelectOption";
import {InscriptionsService} from "../inscriptions.service";
import { CustomFieldsConstants } from 'src/app/constants/CustomFieldsConstants';
import { CountdownEvent } from 'ngx-countdown';
import { DateTime } from 'luxon';
import {WaitingListInfoDTO} from "../../../DTO/WaitingListInfoDTO";
import {ActivatedRoute, Router} from "@angular/router";
import { pushPurchaseEventToGtm } from 'src/app/helpers/gtm.helper';

@Component({
  selector: 'app-inscription-form',
  templateUrl: './inscription-form.component.html',
  styleUrl: './inscription-form.component.scss',
})
export class InscriptionFormComponent implements OnInit, OnDestroy {
    public static readonly MAX_MULTIPLE_INSCRIPTIONS = 20;

    @ViewChild('swiper') swiper!: ElementRef<SwiperContainer>;

    @HostListener('window:resize') onresize() {
        this.isMobile = this.screenSizeService.isScreenSmall();
    }

    @HostListener('window:beforeunload', ['$event']) handleBeforeUnload($event: BeforeUnloadEvent) {
      this.cancelPlaces(false);
    }
    private lastAvailability: any;
    public isLoading = true;
    public submitLoading = false;
    public pricesLoading = false;

    public communityCode: string;

    public formGroup: FormGroup;
    public waitingListInfo : WaitingListInfoDTO;
    public selectedGroupIds: number[] = [];
    public inscription: InscriptionsDTO;
    public inscriptionRegs: InscriptionSentDTO[] = [];
    private ref2: DynamicDialogRef | undefined;
    // Swiper indexes
    public swiperIndex = signal<number>(0);

    //Cuando se activa la reserva de plazas (y su banner de tiempo)
    public activateBannerAtIndex = signal<number>(0);
    public currentStepValid = signal<boolean>(false);

    public hasChildrenFields = computed(() => {
        if (!this.inscription.multipleInscriptionsAtSameTime){
            return false;
        }
        
        return this.selectedGroups().some(g => g.customFields?.length > 0)
            || this.inscription.customFields ?.some(x => x.fieldLocation === 'CHILDREN');
    });

    public lastStepIndex = computed(() => {
        let bannerStep = this.activateBannerAtIndex();

        if (!this.inscription.multipleInscriptionsAtSameTime) {
            // banner == formulario del único inscrito
            // Si no hay multipleInscriptionsAtSameTime, el último paso es el banner de reserva de plazas (el formulario del único inscrito)
            return bannerStep; 
        } else {
            // banner == formulario del inscrito 1
            if (this.hasChildrenFields()){
                // Si hay campos hijos, el último paso es el banner (formulario del inscrito 1) + el número del resto de personas inscritas
                return bannerStep + (this.finalMultipleInscriptionsNum() - 1); 
            } else {
                // Si no hay campos hijos, el último paso es el anterior al banner de reserva de plazas, es decir, el formulario del responsable
                return bannerStep - 1;
            }
        }
    });
    // ---

    public recurrences: RecurrenceDTO[];
    public bonifications: BonificationDTO[];
    public groupOptions: CardSelectOption[] = [];

    public corpColor: string;

    public finalRecurrence: string;
    public isMobile: boolean;

    public RecurrencesTypes = InscriptionsConstants.RecurrencesTypes;
    public prices: InscriptionTotalPricesDTO;

    public submitted = false;
    public fieldTypes = CustomFieldsConstants.InputTypes;
    public slideLoaded = false;

    public generalFreePlaces = signal<number | null>(null);

    //  MultipleInscriptions
    public multipleInscriptionsNumOptions: any[] = [];

    public multipleInscriptionsOption = signal<number>(1);
    public multipleInscriptionsNum = signal<number>(4);
    private multipleInscriptionsNumChanges$ = new Subject<number>();

    effectiveFreePlaces = computed(() => {
        const selectedGroups = this.selectedGroups();
        const general = this.generalFreePlaces();

        // Si no hay grupos o no hay ninguno seleccionado
        if (!this.inscription.groups?.length || !selectedGroups?.length) {
            return general ?? Infinity;
        }

        // Si hay grupos seleccionados → usamos su aforo
        const groupFreePlaces = selectedGroups
            .map(g => g.freePlaces)
            .filter(fp => fp != null);

        if (!groupFreePlaces.length) {
            return general ?? Infinity;
        }

        // Multi-grupo → el mínimo
        return Math.min(...groupFreePlaces);
    });

    public finalMultipleInscriptionsNum = computed(() => {
      const val = this.multipleInscriptionsOption() ?? this.multipleInscriptionsNum();
      return Math.min(val, InscriptionFormComponent.MAX_MULTIPLE_INSCRIPTIONS);
    });

    public maxMultipleInscriptions = computed(() => {
    const { maxMultipleInscriptions: max } = this.inscription;
    const free = this.generalFreePlaces();

    // si no hay límite en la inscripción múltiple, capamos sólo el free
    if (max == null) {
      return free != null
        ? Math.min(free, InscriptionFormComponent.MAX_MULTIPLE_INSCRIPTIONS)
        : free;
    }

    // si sí hay límite, calculamos el menor entre max, free (si existe) y 50
    return Math.min(max, free ?? max, InscriptionFormComponent.MAX_MULTIPLE_INSCRIPTIONS);
  });

    public inscriptionRegsCreated = signal<boolean>(false);
    public responsibleCustomFields: CustomFieldDTO[] = [];
    public defaultBonification: number | null;
    // ---

    public reservedToken = signal<string | null>(null);
    public reservedSeconds = signal<number | null>(null);
    private reservedUntil = signal<DateTime | null>(null);    

    private availabilitySubscription!: Subscription;

    // ---

    public selectedGroups = signal<any[]>([]);

    public termsAccepted = false;
    public termsAcceptedDate: DateTime | null = null;

  constructor(
    private apiService: ApiService,
    public formBuilder: FormBuilder,
    private dialogConfig: DynamicDialogConfig,
    private translate: TranslateService,
    private dialogService: DialogService,
    private communityService: CommunityService,
    private screenSizeService: ScreenSizeService,
    private paymentsService: PaymentsService,
    private inscriptionService: InscriptionsService,
    private df: ChangeDetectorRef,
    public ref: DynamicDialogRef,
    private actRoute: ActivatedRoute,
    private route: Router,
  ) {
        this.multipleInscriptionsNumChanges$
        .pipe(debounceTime(300))
        .subscribe((val) => {
            // sólo tras 300 ms sin nuevos cambios aplicamos al signal
            this.multipleInscriptionsNum.set(val);
        });

        effect(() => {
           this.multipleInscriptionsNumOptions = [
                { label: '1', value: 1 },
            ];
            const max = this.inscription.maxMultipleInscriptions ?? Infinity;

            const freePlaces = this.effectiveFreePlaces();

            if (max >= 2){
                this.multipleInscriptionsNumOptions.push(
                    { label: '2', value: 2, disabled: freePlaces < 2 }
                );
            }

            if (max >= 3){
                this.multipleInscriptionsNumOptions.push(
                    { label: '3', value: 3, disabled: freePlaces < 3 },
                );
            }

            if (max >= 4 && max <= 5) {
                this.multipleInscriptionsNumOptions.push({
                    label: '4',
                    value: 4,
                    disabled: freePlaces < 4
                });

                if (max === 5) {
                    this.multipleInscriptionsNumOptions.push({
                        label: '5',
                        value: 5,
                        disabled: freePlaces < 5
                    });
                }
            }

            if (max > 5) {
                this.multipleInscriptionsNumOptions.push({
                    label: '4 o més',
                    value: null,
                    disabled: freePlaces < 4
                });
            }
            this.updateSwiperHeight();
        });

        effect(() => {
            const num = this.finalMultipleInscriptionsNum();
            const currentLength = this.inscriptionRegs.length;

            if (num > currentLength) {
                for (let i = currentLength; i < num; i++) {
                    let newReg = new InscriptionSentDTO();
                    this.initInscriptionReg(newReg);
                    this.inscriptionRegs.push(newReg);
                }
            } else if (num < currentLength) {
                this.inscriptionRegs.splice(num, currentLength - num);
            }

            this.initCustomFieldsForGroups();

            this.updateSwiperHeight();

            this.recalcPrice();
        });
  }

   async ngOnInit() {
    
    this.inscription = this.dialogConfig.data.inscription;
    this.communityCode = this.dialogConfig.data.route.snapshot.params['community'];
   this.waitingListInfo = this.dialogConfig.data.waitingListInfo;
    this.isMobile = this.screenSizeService.isScreenSmall();

    this.communityService.Get()
    .subscribe((result) => {
      this.corpColor = result.color1;
    });

    this.groupOptions = this.inscription.groups.map(x => ({
      value: x.id,
      title: x.name,
      description: x.description,
        waitingList: this.inscription.waitingList
    } as CardSelectOption));

    if(this.waitingListInfo != null){
        if(this.inscription?.groups?.length){
           this.groupOptions = this.groupOptions.filter(x=>x.value === this.waitingListInfo?.groupId);

        }
    }

    if (this.inscription.multipleInscriptionsAtSameTime){
      this.responsibleCustomFields = this.inscription.customFields.filter(x => x.fieldLocation == "PARENT");
    }

    //Index para activar la reserva de plazas y el contador
    this.activateBannerAtIndex.set([
      this.inscription.groups?.length > 0, //Si hay slide selector de grupos +1
      this.inscription.multipleInscriptionsAtSameTime, //Si hay slide del responsable +1
    ].filter(Boolean).length);

    if (this.activateBannerAtIndex() == 0){
        this.reservePlaces(1);
    }

    /*
    const customFields = await firstValueFrom(this.apiService.getCustomFields(this.communityCode, this.inscription.id));
    this.customFields = customFields.filter(x=> x.publicPermissions === 'rw');
    this.customFields.forEach(customField => {
      this.formGroup.addControl(customField.id.toString(), this._formBuilder.control(null));
    });
    */

    await this.init();

    this.isLoading = false;
    this.slideLoaded = true;

    this.updateSwiperHeight();

    this.subscriptionAvailability();
  }

  private async init() {
    this.formGroup = this.formBuilder.group({});

    // Bonifications
    this.bonifications = await firstValueFrom(this.apiService.getBonificationsInscription(this.communityCode, this.inscription.id));

    // Recurrences
    this.finalRecurrence = this.inscription.defaultRecurrence;

    const recurrences = await firstValueFrom(this.apiService.getRecurrenceInscription(this.communityCode, this.inscription.id));

    let defaultRecurrence = [{
        id: null,
        type: this.inscription.defaultRecurrence,
        discount: 0
    } as RecurrenceDTO];

    this.recurrences = defaultRecurrence.concat(recurrences);

    await this.recalcPrice();
  }

  ngOnDestroy(){
    this.availabilitySubscription?.unsubscribe();
    if (this.reservedToken()) {
      this.cancelPlaces(false);
    }
  }

  public termsAcceptedChange(ev: boolean) {
    this.termsAccepted = ev;

    if(ev) {
      this.termsAcceptedDate = DateTime.now();
    } else {
      this.termsAcceptedDate = null;
    }
  }

  async save(){
    this.submitted = true;



    await this.recalcPrice();

    if (!this.validateAllBonificationsDocumentsUploaded()) return;

    if (!this.formGroup.valid) {
        Swal.fire({
            position: "top" ,
            title: this.translate.instant('GENERIC.bad'),
            icon: 'warning',
            text: this.translate.instant('CheckFormFields'),
            confirmButtonText: this.translate.instant('Continue'),
            customClass: {
                confirmButton: 'swal-confirm-corp'
            },
        });
        return;
    }

    if(!this.termsAccepted) {
      Swal.fire({
        position: "top",
        title: this.translate.instant('INCIDENTS.Atenttion'),
        icon: 'warning',
        text: this.translate.instant('RGPDRequired'),
        confirmButtonText: this.translate.instant('Continue'),
          customClass: {
              confirmButton: 'swal-confirm-corp'
          },
      });
      return;
    }
   
      const inscribingNow = this.inscriptionRegs?.length ?? 0;
      const maxToInscribedNow = this.effectiveFreePlaces();
    console.log('inscribingNow', inscribingNow, 'maxToInscribedNow', maxToInscribedNow);
      const shouldWarn =
          this.waitingListInfo != null &&
          inscribingNow < maxToInscribedNow;

      if (!shouldWarn) {
          this.completeInscription();
          return;
      }

      Swal.fire({
          position: 'top',
          icon: 'warning',
          title: this.translate.instant('WAITING_LIST.LESS_THAN_AVAILABLE_TITLE'),
          html: this.translate.instant('WAITING_LIST.LESS_THAN_AVAILABLE_MESSAGE'),
          showCancelButton: true,
          confirmButtonText: this.translate.instant('WAITING_LIST.CONFIRM_CONTINUE'),
          cancelButtonText: this.translate.instant('WAITING_LIST.CANCEL_EDIT'),
          reverseButtons: true,
          customClass: {
              confirmButton: 'swal-confirm-corp'
          },
      }).then(result => {
          if (result.isConfirmed) {
              this.completeInscription();
          }
      });

  }
  public completeInscription(){
      Swal.fire({
          reverseButtons: true,
          title: this.translate.instant('INSCRIPTIONS.Alert.ConfirmSendTitle'),
          html: this.translate.instant('INSCRIPTIONS.Alert.ConfirmSend', { name: this.inscription.name }),
          icon: 'warning',
          position: "top",
          customClass: {
              confirmButton: 'swal-confirm-corp'
          },
          showCancelButton: true,
          //confirmButtonColor: '#ef5350',
          confirmButtonText: this.translate.instant('Continue'),
          cancelButtonText: this.translate.instant('Cancel'),
          showLoaderOnConfirm: true,
          allowOutsideClick: () => !Swal.isLoading(),
          preConfirm: (result) => {
              this.submitLoading = true;

              let copy = this.prepareInscriptionRegsBeforePriceCalcOrSend();
              return firstValueFrom(this.apiService.sendInscription(
                  this.communityCode,
                  copy,
                  this.reservedToken(),
                  this.termsAcceptedDate,
                  this.waitingListInfo?.accesCode))
                  .then(async (sendInscriptionResult) => {
                      this.processInscriptionSent(sendInscriptionResult);
                      this.submitted = false;
                  })
                  .catch(error => {
                      this.submitLoading = false;
                  });
          }
      }).then((result) => {

      });
  }

  public async recalcPrice() {
    this.pricesLoading = true;

    // Final recurrence
    let recurrence = this.recurrences?.find(x => x.type == this.finalRecurrence);

    if (recurrence) {
        this.finalRecurrence = recurrence.type;
    } else {
        this.finalRecurrence = this.inscription.defaultRecurrence;
    }

    this.inscriptionRegs.forEach(reg => {
        // GROUPS - Configure same groups for all isncriptions
        reg.groupsIds = this.selectedGroupIds;

        // BONIFICATIONS - Bonification document
        if (this.inscription.multipleInscriptionsAtSameTime && !this.inscriptionRegsCreated()){
            reg.bonificationId = this.defaultBonification;
        }

        reg.showBonificationDocumentInput = this.bonifications?.some(b => b.id === reg.bonificationId && b.documentRequired);

        // RECURRENCES - All inscriptions same recurrence
        reg.finalRecurrence = this.finalRecurrence;
    });

    let copy = this.prepareInscriptionRegsBeforePriceCalcOrSend();
    if(this.waitingListInfo != null){
        this.waitingListInfo.inscribingNow = copy?.length;
    }
    this.prices = await this.apiService.calcFinalPrice(this.communityCode, this.inscription.id, copy);
    this.pricesLoading = false;

    this.currentStepValid.set(this.currentStepValidation());
    this.df.detectChanges();


    this.updateSwiperHeight();

  }

  public getUploadedFile(event: UploadedFileModel[], reg: InscriptionSentDTO) {
    reg.bonificationUploaded = event !== null;
  }

  public onSlideChange(ev: any){
    this.swiperIndex.set(this.swiper.nativeElement.swiper.activeIndex);

    // Scroll dialog to top
    this.dialogService.dialogComponentRefMap.forEach(dialog => {
      let el = dialog.location.nativeElement as HTMLElement;
      el.querySelectorAll('.p-dialog-content').forEach(x => {
        x.scrollTop = 0;
      })
    });
  }

  onMultipleInscriptionsNumInput(val: number) {
    this.multipleInscriptionsNumChanges$.next(val);
  }

  private async processInscriptionSent(res: PurchaseResultDTO){

    try {
      let paymentRes = await this.paymentsService.processPurchaseResult(res, this.prices.firstPayment, this.getSecondsRemainging());

      this.submitLoading = false;

      if (paymentRes == PaymentResult.SUCCESS){

        this.trackPurchase(res);
        this.successMessage(this.translate.instant('INSCRIPTIONS.SuccessInscription'));
        if(this.waitingListInfo != null){
            this.apiService
                .updateWaitingListAfterSuccess(this.communityCode, this.waitingListInfo)
                .subscribe({
                    next: (x) => {
                        console.log(x);
                        if (x > 0) {
                            const pending = x;

                            const confirmKey =
                                pending === 1
                                    ? 'WAITING_LIST.CONFIRM_ONE'
                                    : 'WAITING_LIST.CONFIRM_MANY';

                            const descriptionKey =
                                pending === 1
                                    ? 'WAITING_LIST.DESCRIPTION_ONE'
                                    : 'WAITING_LIST.DESCRIPTION_MANY';
                            Swal.fire({
                                title: this.translate.instant('WAITING_LIST.TITLE', {
                                    assigned: this.waitingListInfo.people - x,
                                    requested: this.waitingListInfo.people
                                }),
                                icon: 'question',
                                position: 'top',
                                html: this.translate.instant(descriptionKey, { pending }),
                                showCancelButton: true,
                                confirmButtonText: this.translate.instant(confirmKey, { pending }),
                                cancelButtonText: this.translate.instant('WAITING_LIST.CANCEL'),
                                footer: `<small>${this.translate.instant('WAITING_LIST.FOOTER')}</small>`,
                                reverseButtons: true,
                                allowOutsideClick: false,
                                allowEscapeKey: false,
                                allowEnterKey: false,
                                customClass: {
                                    confirmButton: 'swal-confirm-corp'
                                },

                            }).then(result => {
                                if (result.isConfirmed) {
                                    Swal.fire({
                                        icon: 'success',
                                        title: this.translate.instant('WAITING_LIST.CONTINUE_TITLE'),
                                        text: this.translate.instant('WAITING_LIST.CONTINUE_MESSAGE'),
                                        confirmButtonText: this.translate.instant('Accept'),
                                        customClass: {
                                            confirmButton: 'swal-confirm-corp'
                                        },
                                    }).then(result => {
                                        this.ref.close({
                                            action: 'buyed'
                                        });
                                    });

                                } else if (result.dismiss === Swal.DismissReason.cancel) {
                                    this.apiService
                                        .rejectWaitingLsit(this.communityCode, this.waitingListInfo.id)
                                        .subscribe((x => {
                                            Swal.fire({
                                                icon: 'info',
                                                title: this.translate.instant('WAITING_LIST.CANCELLED_TITLE'),
                                                text: this.translate.instant('WAITING_LIST.CANCELLED_MESSAGE'),
                                                confirmButtonText: this.translate.instant('WAITINGLIST.accordButton'),
                                                customClass: {
                                                    confirmButton: 'swal-confirm-corp'
                                                },
                                            }).then(result => {
                                                this.ref.close({
                                                    action: 'buyed'
                                                });
                                            });

                                        }));
                                }
                            });
                        }else{
                            this.ref.close({
                                action: 'buyed'
                            });
                        }
                    },
                    error: (err) => {
                        console.error('Error actualizando waiting list', err);

                    }
                });

        }



        /*if (res.method){
          this.successMessage(this.translate.instant('PAYMENT.PaymentSuccess'));
        } else {
          this.successMessage(this.translate.instant('INSCRIPTIONS.SuccessInscription'));
        }*/
      } else if (paymentRes == PaymentResult.FAILED){
        this.failedMessage(this.translate.instant('PAYMENT.PaymentFailure'));
        this.cancelPlaces(false);
      } else {
        this.cancelPlaces(false);
        this.ref.close();
      }
    } catch (error) {
      this.failedMessage();
    }
  }

  private trackPurchase(res: PurchaseResultDTO): void {
    if (!res?.purchaseUuid) {
      return;
    }

    const quantity = Math.max(this.inscriptionRegs?.length || 1, 1);
    const value = Number(
      res.totalAmount
      ?? this.prices?.firstPayment?.totalWithFee
      ?? this.prices?.price?.totalWithFee
      ?? 0
    );
    const unitPrice = quantity > 0 ? value / quantity : value;

    pushPurchaseEventToGtm({
      transaction_id: res.purchaseUuid,
      value,
      community_code: this.communityCode,
      items: [{
        item_id: String(this.inscription?.id ?? ''),
        item_name: this.inscription?.name || 'inscription',
        item_category: 'inscription',
        price: unitPrice,
        quantity,
      }],
    });
  }

  private successMessage(title: string){
    //this.submitLoading = false;

    this.dialogService.dialogComponentRefMap.forEach(dialog => {
      dialog.destroy();
    });

    Swal.fire({
      title: title,
      position: "top",
      icon: 'success',
      html: this.inscription.confirmMessage,
      confirmButtonText: this.translate.instant('Accept')
    });

  }

  private async failedMessage(message?: string){
    //this.submitLoading = false;

    Swal.fire({
      title: this.translate.instant('Oops'),
      text: message || this.translate.instant('ERRORS.Generic'),
      icon: 'error',
      confirmButtonText: this.translate.instant('Accept'),
        customClass: {
            confirmButton: 'swal-confirm-corp'
        },
    });
  }

  /** Matrícula: mostrar bloque si la inscripción o algún grupo tiene cuota > 0 (no usar arrow/`some` en la plantilla: NG5002). */
  public showEnrollmentPriceBlock(): boolean {
      if (!this.inscription) {
          return false;
      }
      if ((this.inscription.enrollmentFee ?? 0) > 0) {
          return true;
      }
      const groups = this.inscription.groups;
      if (!groups?.length) {
          return false;
      }
      for (const g of groups) {
          if ((g?.enrollmentFee ?? 0) > 0) {
              return true;
          }
      }
      return false;
  }

  public currentStepValidation(){

      if (this.inscription.groups?.length > 0 && this.swiperIndex() == 0){
          return this.selectedGroupIds?.length > 0;
      } else if (this.inscription.multipleInscriptionsAtSameTime && this.swiperIndex() <= 1){
          return this.finalMultipleInscriptionsNum() > 0;
      } else {
          return true;
      }
  }

    public continue(){
        const valid = this.currentStepValidation();

         if (!valid) {
            Swal.fire({
                title: this.translate.instant('Oops'),
                html: this.translate.instant('SelectOneGroupToContinue'),
                icon: 'warning',
                confirmButtonText: this.translate.instant('Accept'),
                customClass: {
                    confirmButton: 'swal-confirm-corp'
                },
              });
            return;
        }

        if (this.swiperIndex() + 1 == this.activateBannerAtIndex()){ // Si el siguiente paso es el banner de reserva de plazas, reservamos las plazas
            this.inscriptionRegsCreated.set(true);
            this.reservePlaces(this.inscriptionRegs.length, this.selectedGroupIds);
        }

        this.swiper.nativeElement.swiper.slideNext();
    }

    public onGroupsSelect(ev: any) {
        if (this.inscription.groupsAreMultichoice) {
            this.selectedGroupIds = ev.map(x => x.value);
        } else {
            this.selectedGroupIds = [ev.value];
        }

        const selectedGroups = this.inscription.groups
            .filter(g => this.selectedGroupIds.includes(g.id))
            .map(g => {
                const availability = this.lastAvailability?.groups
                    ?.find(a => a.groupId === g.id);

                return {
                    ...g,
                    freePlaces: availability?.freePlaces
                };
            });

        this.selectedGroups.set(selectedGroups);

        this.initCustomFieldsForGroups();
        this.recalcPrice();
    }


    public onDisabledClick(ev: any){

    if (this.inscription.waitingList){
      this.joinWaitingList(ev);
    }

  }

  public async joinWaitingList(ev: any, confirmed = false) {
    console.log(ev);
    if (!confirmed) {
        Swal.fire({
          title: this.translate.instant('Modules.Inscriptions.WAITING_LIST'),
          text: this.translate.instant('Modules.Inscriptions.WaitingList.GroupConfirmationText', { group : ev.title,
          name: this.inscription.name}),
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: this.translate.instant('CONTINUE'),
          cancelButtonText: this.translate.instant('Cancel'),
            customClass: {
                confirmButton: 'swal-confirm-corp'
            },
          reverseButtons: true
        })
        .then((result) => {
          if (result.value) {
            this.joinWaitingList(ev, true);
          }
        });

        return;
    }
      this.ref.close({
          action: 'OPEN_WAITING_LIST',
          group: ev.value
      });

  }


  public async back(toIndex: number = null, cancelPlaces = true) {
        if (this.swiperIndex() > 0) {
            if (toIndex){
                this.swiper.nativeElement.swiper.slideTo(toIndex);
            } else {
                this.swiper.nativeElement.swiper.slidePrev();
            }

            if (this.swiperIndex() < this.activateBannerAtIndex()) {
                if (cancelPlaces) {
                    this.cancelPlaces(true)
                }
                this.subscriptionAvailability();
            }
        } else {
            if (cancelPlaces) {
                this.cancelPlaces(false)
            }
            //return this.currentModal.dismiss(null, 'cancel');
        }
    }

    public countdownEnd(e: CountdownEvent){
        if (e.action !== 'done' || e.left !== 0) {
            return;
        }

        const until = this.reservedUntil();
        if (!until || !this.reservedToken() || DateTime.now() < until) {
            return;
        }

        Swal.fire({
            title: this.translate.instant('Oops'),
            html: this.translate.instant('ReservePlaces.TimeExpired'),
            icon: 'warning',
            confirmButtonText: this.translate.instant('Accept'),
        }).then(() => {
            this.back(this.activateBannerAtIndex() - 1, true);
        });
    }

    private subscriptionAvailability(){
        this.availabilitySubscription = this.inscriptionService
            .subscribeToAvailability(
                this.inscription.id,
                10000,      // intervalo
                !this.waitingListInfo?.accesCode,       // includeWaitingList
                this.waitingListInfo?.accesCode // accessCode
            )
            .subscribe((res: any) => {
                this.lastAvailability = res;
            this.generalFreePlaces.set(res.freePlaces);

            this.groupOptions.forEach(g => {
                    let gAvailability = res.groups.find(x => x.groupId == g.value);

                    if (gAvailability?.freePlaces != null && !this.waitingListInfo){

                        let footer = '';
                        if (gAvailability?.freePlaces > 1){
                            footer =  this.translate.instant('Modules.Inscriptions.OnlyXPlacesLeft', { n: gAvailability?.freePlaces });
                        } else if (gAvailability?.freePlaces == 1){
                            footer = this.translate.instant('Modules.Inscriptions.Only1PlaceLeft');
                        } else {
                            footer = this.translate.instant('Modules.Inscriptions.InscriptionFull');
                        }
                        g.footer = "<b class='text-red'>" + footer + '</b>';
                    } else {
                        g.footer = null;
                    }

                    g.disabled = gAvailability?.freePlaces != null && gAvailability?.freePlaces <= 0;
                });

                this.updateSwiperHeight();
            });
    }

    private updateSwiperHeight(): void {
        setTimeout(() => {
            const swiper = this.swiper?.nativeElement.swiper;
            swiper?.update();
            swiper?.updateAutoHeight(0);
        }, 0);
    }

    private async reservePlaces(places: number, groupsIds: number[] = null){
        await firstValueFrom(this.apiService.Inscriptions_ReservePlaces(this.communityCode, this.inscription.id, {
            places,
            groupsIds,
        }, this.waitingListInfo?.accesCode)).then((res) => {
            this.reservedToken.set(res.token);
            this.reservedSeconds.set(res.seconds);
            this.reservedUntil.set(DateTime.now().plus({ seconds: res.seconds }));
        }).catch((err) => {
              Swal.fire({
                title: this.translate.instant('Oops'),
                html: this.translate.instant('ReservePlaces.NoMorePlacesAvailable'),
                icon: 'warning',
                confirmButtonText: this.translate.instant('Accept'),
                  customClass: {
                      confirmButton: 'swal-confirm-corp'
                  },
            }).then(() => {
                this.back();
            });
        });
    }

    private validateAllBonificationsDocumentsUploaded(){
        this.inscriptionRegs.forEach(x => {
            x.bonificationDocumentInvalid = x.showBonificationDocumentInput && !x.bonificationUploaded;
        });

        let anyDocumentRequired = this.inscriptionRegs.some(x => x.showBonificationDocumentInput && !x.bonificationUploaded);
        if (anyDocumentRequired) {
            Swal.fire({
                title: this.translate.instant('Oops'),
                text: this.translate.instant('INSCRIPTIONS.DocumentRequired'),
                icon: 'warning'
            });
        }
        return !anyDocumentRequired;
    }

    private initInscriptionReg(reg: InscriptionSentDTO){
        reg.inscriptionId = this.inscription.id;
        reg.finalRecurrence = this.inscription.defaultRecurrence;
        reg.bonificationId = this.defaultBonification;

        // Deep copy of customFields
        let customFields = this.inscription.customFields.filter(x => x.publicPermissions.includes('w'));
        if (this.inscription.multipleInscriptionsAtSameTime){
            customFields = customFields.filter(x => x.fieldLocation == "CHILDREN");
        }
        reg.customFields = JSON.parse(JSON.stringify(customFields));
    }

    private prepareInscriptionRegsBeforePriceCalcOrSend(): InscriptionSentDTO[]{
        if(this.waitingListInfo?.userId){
            this.inscriptionRegs.forEach(reg => {
                reg.userId = this.waitingListInfo?.userId
            });
        };
        // Add responsible custom fields
        let copy = JSON.parse(JSON.stringify(this.inscriptionRegs));
        this.addResponsibleCustomFields(copy);

        copy.rgpdAcceptedDate = this.termsAcceptedDate ? this.termsAcceptedDate.toISO() : null;

        if(this.waitingListInfo != null){
            copy.waitingListInfoDTO = this.waitingListInfo;
        }

        return copy;
    }

    private addResponsibleCustomFields(regs: InscriptionSentDTO[]){
      if (this.inscription.multipleInscriptionsAtSameTime){
          regs.forEach(reg => {
              const existingIds = new Set(reg.customFields.map(field => field.id));

              const missingFields = this.responsibleCustomFields.filter(
                  field => !existingIds.has(field.id)
              );

              if (missingFields.length) {
                  reg.customFields.unshift(...missingFields);
              }
          });
      }
    }

    private async cancelPlaces(awaitRequest = true){
        const token = this.reservedToken();
        if (!token) {
            return;
        }

        const executeCancelPlacesRequest = () =>
            firstValueFrom(this.apiService.Inscriptions_CancelPlaces(this.communityCode, this.inscription.id, token));

        if (awaitRequest) {
            await executeCancelPlacesRequest();
        } else {
            executeCancelPlacesRequest();
        }

        this.reservedToken.set(null);
        this.reservedSeconds.set(null);
        this.reservedUntil.set(null);
    }

    private initCustomFieldsForGroups(){
      this.inscriptionRegs.forEach(reg => {
        // Deep copy of group customFields
        let map = this.selectedGroups().map(x => ({
          id: x.id,
          name: x.name,
          customFieldsValues: x.customFields.map(y => {
            y.customFieldId= y.id;
            return y;
          }),
        }));
        reg.groups = JSON.parse(JSON.stringify(map));
      });
    }

    private getSecondsRemainging(): number {
      if (this.reservedUntil()){
        const diff = this.reservedUntil().diffNow('seconds').seconds;
        return diff > 0 ? Math.ceil(diff) : 0;
      }
    }

    public shouldShowPriceFromPrefix(): boolean {
        if (this.coercePriceVisibility(this.inscription?.priceVisibility) !== 'ShowFrom') {
            return false;
        }

        if (this.inscription.groups?.length > 0) {
            return this.swiperIndex() === 0 || this.selectedGroupIds.length === 0;
        }

        return this.swiperIndex() < this.lastStepIndex();
    }

    private coercePriceVisibility(value: unknown): 'Show' | 'ShowFrom' | 'Hide' {
        if (value === 'Show' || value === 'ShowFrom' || value === 'Hide') {
            return value;
        }
        if (value === 1 || value === '1') {
            return 'ShowFrom';
        }
        if (value === 2 || value === '2') {
            return 'Hide';
        }
        return 'Show';
    }
}
